import { EntityMediaZip } from '@arhs-fleetback/types/entities/media';

import { KEYLOOP_API_URL } from 'constants/ApiConstants';
import { INTERVENTIONS_API_URL } from 'constants/InterventionConstants';
import { MEDIA_PATH_ENUM, MEDIA_TYPE } from 'constants/MediaConstants';
import { SALES, TECHNICIAN } from 'constants/ModeConstants';
import type { Action, ErrorAction, GetRequest, PatchRequest, PostRequest, PutRequest } from 'modules/global/generic.types';
import type { ModeEnum } from 'modules/global/typing/enums/modes.enum';
import * as actionTypes from 'modules/interventions/actionTypes';
import { generateZip } from 'modules/medias/actions';

import type * as types from '../types/actions';
import type { Inspection } from '../types/inspections';
import type { DetailedIntervention } from '../types/projections/detailed-intervention';
import type { InterventionReminderCreate } from '../types/requests/intervention-reminder-create';
import type { InterventionsAcceptManuallyRequest } from '../types/requests/interventions-accept-manually-request';

// STATE & SELECTION

export const selectIntervention = (interventionId: string, meta: Record<string, unknown> = {}): Action<string> => ({
  type: actionTypes.SELECT_INTERVENTION,
  payload: interventionId,
  meta,
});

export const interventionIsReady = (meta: Record<string, unknown> = {}): types.InterventionIsReadyAction => ({
  type: actionTypes.INTERVENTION_IS_READY,
  meta,
});

// MODALS MANAGEMENT

export const openDuplicateModal = (type: string): Action<string> => ({
  type: actionTypes.OPEN_DUPLICATE_MODAL,
  payload: type,
});

export const closeDuplicateModal = (): Action<undefined> => ({
  type: actionTypes.CLOSE_DUPLICATE_MODAL,
});

export const openUniqueVideoModal = (): Action<undefined> => ({
  type: actionTypes.OPEN_UNIQUE_VIDEO_MODAL,
});

export const closeUniqueVideoModal = (): Action<undefined> => ({
  type: actionTypes.CLOSE_UNIQUE_VIDEO_MODAL,
});

export const openApproveManuallyModal = (): types.OpenApproveManuallyModalAction => ({
  type: actionTypes.OPEN_APPROVE_MANUALLY_MODAL,
});

export const closeApproveManuallyModal = (): types.CloseApproveManuallyModalAction => ({
  type: actionTypes.CLOSE_APPROVE_MANUALLY_MODAL,
});

export const openSendModal = (): types.OpenSendModalAction => ({
  type: actionTypes.OPEN_SEND_MODAL,
});

export const closeSendModal = (): types.CloseSendModalAction => ({
  type: actionTypes.CLOSE_SEND_MODAL,
});

export const openImportTScans = (meta: Record<string, unknown> = {}): types.OpenImportTScansModalAction => ({
  type: actionTypes.OPEN_TSCANS_IMPORT_MODAL,
  meta,
});

export const closeImportTScans = (meta: Record<string, unknown> = {}): types.CloseImportTScansModalAction => ({
  type: actionTypes.CLOSE_TSCANS_IMPORT_MODAL,
  meta,
});

export const openImportAudits = (meta: Record<string, unknown> = {}): types.OpenImportAuditsModalAction => ({
  type: actionTypes.OPEN_AUDITS_IMPORT_MODAL,
  meta,
});

export const closeImportAudits = (meta: Record<string, unknown> = {}): types.CloseImportAuditsModalAction => ({
  type: actionTypes.CLOSE_AUDITS_IMPORT_MODAL,
  meta,
});

export const openChecklistModal = (
  payload: { checklistGroupId: string; mode: string },
  meta: Record<string, unknown> = {},
): Action<{ checklistGroupId: string; mode: string }> => ({
  type: actionTypes.OPEN_CHECKLISTS_IMPORT_MODAL,
  payload,
  meta,
});

export const closeChecklistModal = (): types.CloseImportChecklistsAction => ({
  type: actionTypes.CLOSE_CHECKLISTS_IMPORT_MODAL,
});

export const openChecklistGroupModal = (meta: Record<string, unknown> = {}): Action<undefined> => ({
  type: actionTypes.OPEN_CHECKLISTGROUP_MODAL,
  meta,
});

export const closeChecklistGroupModal = (meta: Record<string, unknown> = {}): Action<undefined> => ({
  type: actionTypes.CLOSE_CHECKLISTGROUP_MODAL,
  meta,
});

export const openMobilityModal = (
  payload: { mobilityId: string; interventionId: string },
  meta: Record<string, unknown> = {},
): types.OpenMobilityModalAction => ({
  type: actionTypes.OPEN_MOBILITY_MODAL,
  payload,
  meta,
});

export const closeMobilityModal = (meta: Record<string, unknown> = {}): types.CloseMobilityModalAction => ({
  type: actionTypes.CLOSE_MOBILITY_MODAL,
  meta,
});

export const openMobilityModalReady = (
  mobility: import('modules/selfServices/types').Mobility,
  meta: Record<string, unknown> = {},
): types.OpenMobilityModalReadyAction => ({
  type: actionTypes.OPEN_MOBILITY_MODAL_READY,
  payload: mobility,
  meta,
});

export const closeWorkOrderModal = (): types.CloseWorkOrderModal => ({
  type: actionTypes.CLOSE_WORK_ORDER_MODAL,
});

export const closeWorkOrderErrorModal = (): types.CloseWorkOrderErrorModal => ({
  type: actionTypes.CLOSE_WORK_ORDER_ERROR_MODAL,
});

export const openImportWorksModal = (): Action<undefined> => ({ type: actionTypes.OPEN_IMPORT_WORKS_MODAL });

export const closeImportWorksModal = (): Action<undefined> => ({ type: actionTypes.CLOSE_IMPORT_WORKS_MODAL });

export const openResendModal = (meta: Record<string, unknown> = {}): Action<undefined> => ({
  type: actionTypes.OPEN_RESEND_MODAL,
  meta,
});

export const closeResendModal = (meta: Record<string, unknown> = {}): Action<undefined> => ({
  type: actionTypes.CLOSE_RESEND_MODAL,
  meta,
});

// IMPORTS

export const importAuditsOrTscans = (
  interventionId: string,
  mode: ModeEnum,
  entity: 'audits' | 'tscans' | 'tireQuotes',
  entityIds: string[],
  meta: Record<string, unknown> = {},
): Action<PostRequest<{ ids: string[] }> & { entity: 'audits' | 'tscans' | 'tireQuotes' }> => ({
  type: actionTypes.IMPORT_AUDITS_OR_TSCANS,
  payload: {
    request: {
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/${entity}/import`,
      method: 'POST',
      data: { ids: entityIds },
    },
    entity,
  },
  meta,
});

export const importAuditsOrTscansSuccess = (
  intervention: DetailedIntervention,
  importedIds: string[],
  meta: Record<string, unknown> = {},
): Action<{ intervention: DetailedIntervention; importedIds: string[] }> => ({
  type: actionTypes.IMPORT_AUDITS_OR_TSCANS_SUCCESS,
  payload: { intervention, importedIds },
  meta,
});

export const importAuditsOrTscansFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): types.ImportAuditOrTScansFailureAction => ({
  type: actionTypes.IMPORT_AUDITS_OR_TSCANS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const importChecklistsGroups = (
  entityId: string,
  mode: ModeEnum,
  data: types.ImportChecklistsPayload,
  meta: Record<string, unknown> = {},
): types.ImportInterventionChecklistsAction => ({
  type: actionTypes.IMPORT_CHECKLISTS_GROUPS,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${entityId}/checklistGroups`,
      data,
    },
  },
  meta,
});

export const importChecklistsGroupsSuccess = (
  { checklists, checklistGroup, checklistGroups }: types.ImportInterventionChecklistsSuccessPayload,
  meta?: Record<string, unknown>,
): types.ImportInterventionChecklistsSuccessAction => ({
  type: actionTypes.IMPORT_CHECKLISTS_GROUPS_SUCCESS,
  payload: {
    checklists,
    checklistGroup,
    checklistGroups,
  },
  meta,
});

export const importChecklistsGroupsFailure = (
  error: string,
  meta?: Record<string, unknown>,
): types.ImportInterventionChecklistsFailureAction => ({
  type: actionTypes.IMPORT_CHECKLISTS_GROUPS_FAILURE,
  payload: error,
  meta,
  error: true,
});

export const importChecklists = (
  interventionId: string,
  mode: ModeEnum,
  data: types.ImportChecklistPayload,
  meta: Record<string, unknown> = {},
): types.ImportChecklistAction => ({
  type: actionTypes.IMPORT_CHECKLISTS,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/checklists/import`,
      data,
    },
  },
  meta,
});

export const importChecklistsSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): Action<DetailedIntervention> => ({
  type: actionTypes.IMPORT_CHECKLISTS_SUCCESS,
  payload: intervention,
  meta,
});

export const importChecklistsFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): types.ImportChecklistFailureAction => ({
  type: actionTypes.IMPORT_CHECKLISTS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const importWorksIntoIntervention = (
  interventionId: string,
  mode: ModeEnum,
  works: types.Works[],
  meta: Record<string, unknown> = {},
): Action<PostRequest<{ works: types.Works[] }> & { interventionId: string }> => ({
  type: actionTypes.IMPORT_WORKS_INTO_INTERVENTION,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/workOrder/import`,
      data: { works },
    },
    interventionId,
  },
  meta,
});

export const importWorksIntoInterventionSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): Action<DetailedIntervention> => ({
  type: actionTypes.IMPORT_WORKS_INTO_INTERVENTION_SUCCESS,
  payload: intervention,
  meta,
});

export const importWorksIntoInterventionFailure = (error: string): types.ImportWorksIntoInterventionActionFailure => ({
  type: actionTypes.IMPORT_WORKS_INTO_INTERVENTION_FAILURE,
  payload: error,
  error: true,
});

export const setShowImportedWork = (checked: boolean): types.SetShowImportedWork => ({
  type: actionTypes.SET_SHOW_IMPORTED_WORK,
  payload: checked,
});

export const importTireReport = (
  interventionId: string,
  mode: ModeEnum,
  data: types.ImportTireReportPayload = {},
  meta: Record<string, unknown> = {},
): types.ImportTireReportAction => ({
  type: actionTypes.IMPORT_TIRE_REPORT,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/tireReport/import`,
      data,
    },
  },
  meta,
});

export const setEditedTire = (position: unknown): Action<unknown> => ({
  type: actionTypes.SET_EDITED_TIRE,
  payload: position,
});

export const resetEditedTire = (): Action<undefined> => ({
  type: actionTypes.RESET_EDITED_TIRE,
});

// SHARE & COMMUNICATION

export const resetShareInterventionLink = (_: unknown, meta?: Record<string, unknown>): Action<undefined> => ({
  type: actionTypes.RESET_INTERVENTION_LINK_REQUEST,
  meta,
});

export const shareInterventionLink = (
  {
    interventionId,
    mode,
    shorten,
    preview,
    proview,
  }: { interventionId: string; mode: ModeEnum; shorten?: boolean; preview?: boolean; proview?: boolean },
  meta?: Record<string, unknown>,
): Action<GetRequest<{ shorten?: boolean; preview?: boolean; proview?: boolean }> & { mode: ModeEnum; id: string }> => ({
  type: actionTypes.FETCH_INTERVENTION_LINK_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/link`,
      params: {
        shorten,
        preview,
        proview,
      },
    },
    mode,
    id: interventionId,
  },
  meta,
});

export const shareInterventionLinkSuccess = (link: unknown, meta: Record<string, unknown> = {}): Action<unknown> => ({
  type: actionTypes.FETCH_INTERVENTION_LINK_SUCCESS,
  payload: link,
  meta,
});

export const shareInterventionLinkFailure = (message: unknown, meta: Record<string, unknown> = {}): Action<unknown> => ({
  type: actionTypes.FETCH_INTERVENTION_LINK_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const sendUniqueVideo = (
  interventionId: string,
  params: Record<string, unknown>,
  meta: Record<string, unknown> = {},
): Action<PostRequest<undefined, Record<string, unknown>> & { interventionId: string }> => ({
  type: actionTypes.SEND_UNIQUE_VIDEO_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[SALES]}/${interventionId}/concatVideos`,
      params,
    },
    interventionId,
  },
  meta,
});

export const sendUniqueVideoSuccess = (
  interventionId: string,
  meta: Record<string, unknown> = {},
): Action<string> => ({
  type: actionTypes.SEND_UNIQUE_VIDEO_SUCCESS,
  payload: interventionId,
  meta,
});

export const sendUniqueVideoFailure = (error: unknown, meta: Record<string, unknown> = {}): Action<unknown> => ({
  type: actionTypes.SEND_UNIQUE_VIDEO_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const readInterventionMessages = (
  id: string,
  mode: ModeEnum,
  meta: Record<string, unknown> = {},
): Action<{ id: string } & PatchRequest<undefined>> => ({
  type: actionTypes.READ_INTERVENTION_MESSAGES_REQUEST,
  payload: {
    id,
    request: {
      method: 'PATCH',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${id}/messages/read`,
    },
  },
  meta,
});

export const readInterventionMessagesSuccess = (
  id: string,
  meta: Record<string, unknown> = {},
): Action<{ id: string }> => ({
  type: actionTypes.READ_INTERVENTION_MESSAGES_SUCCESS,
  payload: { id },
  meta,
});

export const readInterventionMessagesFailure = (
  message: unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.READ_INTERVENTION_MESSAGES_FAILURE,
  payload: message,
  error: true,
  meta,
});

export const generateAllFilesZip = (
  interventionId: string,
  mode: ModeEnum,
  meta: Record<string, unknown> = {},
): ReturnType<typeof generateZip> => generateZip(
  {
    entityId: interventionId,
    entityType: EntityMediaZip.INTERVENTION,
    mode,
    mediaType: MEDIA_TYPE.ALL,
    mediaPathExtension: MEDIA_PATH_ENUM.PATH,
  },
  meta,
);

export const printFOQRCode = (
  interventionId: string,
  mode: ModeEnum,
  filename: string,
  meta: Record<string, unknown> = {},
): types.PrintFOQRCodeAction => ({
  type: actionTypes.PRINT_FO_QR_CODE,
  payload: {
    mode,
    filename,
    interventionId,
  },
  meta,
});

// SPECIFIC OPERATIONS

export const approveManually = (
  interventionId: string,
  mode: ModeEnum,
  data: Partial<InterventionsAcceptManuallyRequest>,
  checklistGroupId?: string,
): Action<PostRequest<Partial<InterventionsAcceptManuallyRequest>>> => {
  const url = !checklistGroupId
    ? `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/accept`
    : `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/checklistGroups/${checklistGroupId}/accept`;

  return {
    type: actionTypes.APPROVE_MANUALLY_REQUEST,
    payload: {
      request: {
        method: 'POST',
        url,
        data,
      },
    },
  };
};

export const approveManuallySuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.approveManuallySuccessAction => ({
  type: actionTypes.APPROVE_MANUALLY_SUCCESS,
  payload: intervention,
  meta,
});

export const approveManuallyFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): types.approveManuallyFailureAction => ({
  type: actionTypes.APPROVE_MANUALLY_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const clearCreateReminderError = (): Action<undefined> => ({
  type: actionTypes.CLEAR_CREATE_INTERVENTION_REMINDER_ERROR,
});

export const delayReminderRequest = (
  interventionId: string,
  mode: ModeEnum,
  sendDate: string,
  meta: Record<string, unknown> = {},
): Action<PatchRequest<{ sendDate: string }>> => ({
  type: actionTypes.DELAY_REMINDER_REQUEST,
  payload: {
    request: {
      method: 'PATCH',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/reminders/sendDate`,
      data: { sendDate },
    },
  },
  meta,
});

export const delayReminderSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): Action<DetailedIntervention> => ({
  type: actionTypes.DELAY_REMINDER_SUCCESS,
  payload: intervention,
  meta,
});

export const delayReminderFailure = (error: unknown, meta: Record<string, unknown> = {}): Action<unknown> => ({
  type: actionTypes.DELAY_REMINDER_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const rebrand = (interventionId: string, mode: ModeEnum, meta?: Record<string, unknown>): types.RebrandAction => ({
  type: actionTypes.REBRAND_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/medias/transform`,
    },
  },
  meta,
});

export const rebrandSuccess = (payload: unknown, meta?: Record<string, unknown>): Action<unknown> => ({
  type: actionTypes.REBRAND_SUCCESS,
  payload,
  meta,
});

export const rebrandFailure = (error: string): types.RebrandFailureAction => ({
  type: actionTypes.REBRAND_FAILURE,
  payload: error,
  error: true,
});

export const patchFinalInvoiceStatus = (
  interventionId: string,
  mode: ModeEnum,
  invoiceStatus: string,
  meta: Record<string, unknown> = {},
): types.PatchFinalInvoiceStatusAction => ({
  type: actionTypes.INTERVENTION_INVOICE_PATCH_STATUS,
  payload: {
    request: {
      method: 'PATCH',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/finalInvoice/status`,
      data: {
        status: invoiceStatus,
      },
    },
  },
  meta,
});

export const patchFinalInvoiceStatusSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.PatchFinalInvoiceStatusSuccessAction => ({
  type: actionTypes.INTERVENTION_INVOICE_PATCH_STATUS_SUCCESS,
  payload: intervention,
  meta,
});

export const patchFinalInvoiceStatusFailure = (
  error: Error | string | unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.INTERVENTION_INVOICE_PATCH_STATUS_FAILURE,
  payload: error,
  meta,
});

export const mediariesExport = (
  interventionId: string,
  mode: ModeEnum,
  integrations: string[],
  meta: Record<string, unknown> = {},
): types.MediariesExportAction => ({
  type: actionTypes.MEDIARIES_EXPORT,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/integrations/export`,
      data: { integrations },
    },
  },
  meta,
});

export const mediariesExportSuccess = (
  integrations: string[],
  meta: Record<string, unknown> = {},
): types.MediariesExportSuccessAction => ({
  type: actionTypes.MEDIARIES_EXPORT_SUCCESS,
  payload: integrations,
  meta,
});

export const mediariesExportFailure = (
  error: string,
  integrations: string[],
  meta: Record<string, unknown> = {},
): types.MediariesExportFailureAction => ({
  type: actionTypes.MEDIARIES_EXPORT_FAILURE,
  payload: { error, integrations },
  error: true,
  meta,
});

export const followFile = (
  mode: ModeEnum,
  interventionId: string,
  meta: Record<string, unknown> = {},
): Action<PostRequest<undefined>> => ({
  type: actionTypes.FOLLOW_FILE,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/follow`,
    },
  },
  meta,
});

export const unfollowFile = (
  mode: ModeEnum,
  interventionId: string,
  meta: Record<string, unknown> = {},
): Action<PostRequest<undefined>> => ({
  type: actionTypes.UNFOLLOW_FILE,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/unfollow`,
    },
  },
  meta,
});

export const pushBackDMS = (interventionId: string, meta: Record<string, unknown> = {}): types.PushBackDMSActionAction => ({
  type: actionTypes.PUSH_BACK_DMS_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${KEYLOOP_API_URL}/${interventionId}`,
    },
  },
  meta,
});

// CORE ACTIONS

export const createInterventionReminder = (
  { mode, ...data }: { mode: ModeEnum; id?: string } & Partial<InterventionReminderCreate>,
  meta: Record<string, unknown> = {},
): Action<PostRequest<InterventionReminderCreate>> => ({
  type: actionTypes.CREATE_INTERVENTION_REMINDER_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${data?.id}/reminders`,
      data: {
        type: 'CHECKLIST',
        ...data,
      },
    },
  },
  meta,
});

export const createInterventionReminderSuccess = (
  reminder: unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.CREATE_INTERVENTION_REMINDER_SUCCESS,
  payload: reminder,
  meta,
});

export const createInterventionReminderFailure = (
  error: unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.CREATE_INTERVENTION_REMINDER_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchInterventionPaymentHistory = (id: string): Action<{ id: string }> => ({
  type: actionTypes.FETCH_INTERVENTION_PAYMENT_HISTORY_REQUEST,
  payload: { id },
});

export const fetchInterventionPaymentHistorySuccess = (history: unknown): Action<unknown> => ({
  type: actionTypes.FETCH_INTERVENTION_PAYMENT_HISTORY_SUCCESS,
  payload: history,
});

export const fetchInterventionPaymentHistoryFailure = (error: unknown): Action<unknown> => ({
  type: actionTypes.FETCH_INTERVENTION_PAYMENT_HISTORY_FAILURE,
  payload: error,
  error: true,
});

export const fetchInterventionPdfRequest = (
  interventionId: string,
  mode: ModeEnum,
  name: string,
  meta: Record<string, unknown> = {},
): Action<{ name: string } & GetRequest<undefined>> => ({
  type: actionTypes.FETCH_INTERVENTIONS_PDF,
  payload: {
    name,
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/pdf`,
    },
  },
  meta,
});

export const fetchInterventionLinkedFilePdfRequest = (
  interventionId: string,
  name: string,
  meta: Record<string, unknown> = {},
): Action<{ name: string } & GetRequest<undefined>> => ({
  type: actionTypes.FETCH_INTERVENTIONS_PDF,
  payload: {
    name,
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[TECHNICIAN]}/${interventionId}/linkedFilePdf`,
    },
  },
  meta,
});

export const fetchInterventionCustomerPdfRequest = (
  interventionId: string,
  mode: ModeEnum,
  name: string,
  meta: Record<string, unknown> = {},
): Action<{ name: string } & GetRequest<undefined>> => ({
  type: actionTypes.FETCH_INTERVENTIONS_PDF,
  payload: {
    name,
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/pdf/FO`,
    },
  },
  meta,
});

export const fetchInterventionPdfRequestSuccess = (
  payload: unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.FETCH_INTERVENTIONS_PDF_SUCCESS,
  payload,
  meta,
});

export const fetchInterventionPdfRequestError = (
  error: unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.FETCH_INTERVENTIONS_PDF_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const updateKioskPreferences = (
  { interventionId, mode, kioskPreference: data }: { interventionId: string; mode: ModeEnum; kioskPreference: unknown },
  meta: Record<string, unknown> = {},
): Action<PutRequest<unknown>> => ({
  type: actionTypes.UPDATE_KIOSK_PREFERENCES,
  payload: {
    request: {
      method: 'PUT',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/kioskPreference`,
      data,
    },
  },
  meta,
});

export const createStockFileChecklists = (
  { interventionId, brandId }: { interventionId: string; brandId: string },
  meta: Record<string, unknown> = {},
): Action<PostRequest<{ brandId: string }>> => ({
  type: actionTypes.CREATE_DEFAULT_STOCK_CHECKLIST_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[SALES]}/${interventionId}/defaultChecklists`,
      data: { brandId },
    },
  },
  meta,
});

export const createStockFileChecklistsSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): Action<DetailedIntervention> => ({
  type: actionTypes.CREATE_DEFAULT_STOCK_CHECKLIST_SUCCESS,
  payload: intervention,
  meta,
});

export const createStockFileChecklistsFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): ErrorAction => ({
  type: actionTypes.CREATE_DEFAULT_STOCK_CHECKLIST_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const fetchLinkedInterventions = (
  mode: ModeEnum,
  interventionId: string,
  meta: Record<string, unknown> = {},
): Action<GetRequest<undefined>> => ({
  type: actionTypes.FETCH_LINKED_INTERVENTIONS_REQUEST,
  payload: {
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/links`,
    },
  },
  meta,
});

export const fetchLinkedInterventionsSuccess = (
  interventions: unknown[],
  meta: Record<string, unknown> = {},
): Action<{ content: unknown[] }> => ({
  type: actionTypes.FETCH_LINKED_INTERVENTIONS_SUCCESS,
  payload: { content: interventions },
  meta,
});

export const fetchLinkedInterventionsFailure = (
  payload: unknown,
  meta: Record<string, unknown> = {},
): Action<unknown> => ({
  type: actionTypes.FETCH_LINKED_INTERVENTIONS_FAILURE,
  payload,
  error: true,
  meta,
});

export const sendInterventionRequest = (
  { id, channels, force, mode }: { id: string; mode: ModeEnum } & types.SendInterventionPayloadAction,
  meta: Record<string, unknown> = {},
): types.SendInterventionAction => ({
  type: actionTypes.SEND_INTERVENTION_REQUEST,
  payload: {
    request: {
      method: 'POST',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${id}/send`,
      data: { channels, force },
    },
    id,
    types: channels,
  },
  meta,
});

export const sendInterventionSuccess = (
  intervention: DetailedIntervention,
  meta: Record<string, unknown> = {},
): types.SendInterventionSuccessAction => ({
  type: actionTypes.SEND_INTERVENTION_SUCCESS,
  payload: intervention,
  meta,
});

export const sendInterventionFailure = (
  message: string,
  meta: Record<string, unknown> = {},
): types.SendInterventionFailureAction => ({
  type: actionTypes.SEND_INTERVENTION_FAILURE,
  payload: message,
  error: true,
  meta,
});

// INSPECTIONS

export const fetchInspections = (
  interventionId: string,
  mode: ModeEnum,
  meta: Record<string, unknown> = {},
): types.FetchInspectionsAction => ({
  type: actionTypes.FETCH_INSPECTIONS,
  payload: {
    interventionId,
    request: {
      method: 'GET',
      url: `${INTERVENTIONS_API_URL[mode as keyof typeof INTERVENTIONS_API_URL]}/${interventionId}/inspections`,
    },
  },
  meta,
});

export const fetchInspectionsSuccess = (
  interventionId: string,
  inspections: Inspection[],
  meta: Record<string, unknown> = {},
): types.FetchInspectionsSuccessAction => ({
  type: actionTypes.FETCH_INSPECTIONS_SUCCESS,
  payload: { interventionId, inspections },
  meta,
});

export const fetchInspectionsFailure = (
  error: string,
  meta: Record<string, unknown> = {},
): types.FetchInspectionsFailureAction => ({
  type: actionTypes.FETCH_INSPECTIONS_FAILURE,
  payload: error,
  error: true,
  meta,
});

export const openAssignInspectionModal = (
  inspectionId: string,
  meta: Record<string, unknown> = {},
): types.OpenAssignInspectionModalAction => ({
  type: actionTypes.OPEN_ASSIGN_INSPECTION_MODAL,
  payload: inspectionId,
  meta,
});

export const closeAssignInspectionModal = (
  meta: Record<string, unknown> = {},
): types.CloseAssignInspectionModalAction => ({
  type: actionTypes.CLOSE_ASSIGN_INSPECTION_MODAL,
  meta,
});
